import { FilterTrayTabId } from '@ecosystem/newron-design-system/build/exports/containers';
import { deepEqual } from '../tools/object';
import { Context, Entity, GenericWarning, ModalDsl, PageDsl } from '../types';
import { CorePageAction } from './actions';
import { DataInfo, PageInfo } from './types';
import { TrayType } from '../types/content/custom.types';

type ContextState = {
  settings: Record<string, Entity[]>;
  filters: Record<string, Entity[]>;
};

export type CorePageState = {
  pageStamp: number;
  contextState?: ContextState;
  contextLoading?: boolean;
  pendingContextState?: ContextState;
  pageExpired: boolean;
  page?: PageInfo;
  data?: DataInfo;
  dsl?: PageDsl;
  error?: string;
  dslLoading?: boolean;
  dataLoading?: boolean;
  modalDsl?: ModalDsl;
  globalWarnings: GenericWarning[];
  pageWarnings: GenericWarning[];
  trayType?: TrayType;
  customizationTrayTabId?: FilterTrayTabId;
  isFilterTray?: boolean;
  isDslLoadedInCollection?: boolean;
};

function initialState(): CorePageState {
  return {
    pageStamp: -1,
    pageExpired: true,
    globalWarnings: [],
    pageWarnings: [],
  };
}

function reducer(state: CorePageState, action: CorePageAction): CorePageState {
  const { type, payload } = action;

  switch (type) {
    case 'SET_CONTEXT_LOADING':
      return {
        ...state,
        contextLoading: payload,
      };
    case 'SET_CONTEXT_STATE':
      return setContextState(state, payload);
    case 'SET_PAGE':
      return setPageState(state, payload);
    case 'SET_DATA':
      return setDataInfo(state, payload);
    case 'SET_DSL':
      return setPageDsl(state, payload);
    case 'SET_GLOBAL_WARNINGS':
      return setGlobalWarnings(state, payload);
    case 'RESET_GLOBAL_WARNINGS':
      return resetGlobalWarnings(state, true);
    case 'SET_PAGE_WARNINGS':
      return setPageWarnings(state, payload);
    case 'RESET_PAGE_WARNINGS':
      return resetPageWarnings(state);
    case 'SET_DATA_LOADING':
      return setIsDataLoading(state, payload);
    case 'START_DSL_FETCH':
      return {
        ...state,
        dslLoading: true,
        error: undefined,
      };
    case 'SET_ERROR':
      return {
        ...state,
        dslLoading: false,
        error: payload,
      };
    case 'EXPIRE_PAGE':
      return {
        ...state,
        pageExpired: true,
        error: undefined,
        pendingContextState: state.contextState,
      };
    case 'SET_MODAL_DSL':
      return {
        ...state,
        modalDsl: payload,
      };
    case 'SET_CUSTOMIZATION_TRAY_TYPE':
      return {
        ...state,
        trayType: action.payload,
      };
    case 'SET_CUSTOMIZATION_TRAY_TAB_ID':
      return {
        ...state,
        customizationTrayTabId: payload,
      };
    case 'SET_IS_FILTER_TRAY':
      return {
        ...state,
        isFilterTray: payload,
      };

    case 'SET_IS_DSL_LOADED_FOR_COLLECTION':
      return {
        ...state,
        isDslLoadedInCollection: payload,
      };
  }
}

function getUniqueWarnings(allWarnings: GenericWarning[]) {
  const keySplitter = '__key__';
  const getWarningKey = (warning: GenericWarning) => `${warning.message}${keySplitter}${warning.level}`;

  const uniqueMessageLevelCombos = new Set(allWarnings.map(getWarningKey));

  return Array.from(uniqueMessageLevelCombos).map(key => {
    const [message, level] = key.split(keySplitter);
    return allWarnings.find(warning => warning.message === message && warning.level === level);
  });
}

function resetGlobalWarnings(state: CorePageState, resetWarnings: boolean) {
  if (resetWarnings) {
    return { ...state, globalWarnings: [] };
  }

  return { ...state };
}

function resetPageWarnings(state: CorePageState) {
  return { ...state, pageWarnings: [] };
}

function setGlobalWarnings(state: CorePageState, globalWarnings: GenericWarning[]) {
  if (!globalWarnings.length) {
    return { ...state };
  }

  const allWarnings = [...state.globalWarnings, ...globalWarnings];

  return {
    ...state,
    globalWarnings: getUniqueWarnings(allWarnings),
  };
}

function setPageWarnings(state: CorePageState, pageWarnings: GenericWarning[]) {
  if (!pageWarnings.length) {
    return { ...state };
  }

  const allWarnings = [...state.pageWarnings, ...pageWarnings];

  return {
    ...state,
    pageWarnings: getUniqueWarnings(allWarnings),
  };
}

function setIsDataLoading(state: CorePageState, payload: boolean): CorePageState {
  return {
    ...state,
    dataLoading: payload,
  };
}

function setPageDsl(state: CorePageState, dsl: PageDsl): CorePageState {
  return {
    ...state,
    pageStamp: Date.now(),
    dsl: dsl,
    dslLoading: false,
    error: undefined,
    pageExpired: false,
    contextState: state.pendingContextState,
    pendingContextState: undefined,
  };
}

function setDataInfo(state: CorePageState, info: DataInfo): CorePageState {
  if (deepEqual(state.data, info)) {
    return state;
  }

  return {
    ...state,
    data: info,
  };
}

function setPageState(state: CorePageState, info: PageInfo): CorePageState {
  if (deepEqual(state.page, info)) {
    return state;
  }

  return {
    ...state,
    page: info,
    pendingContextState: state.contextState,
    error: undefined,
    pageExpired: true,
  };
}

function setContextState(state: CorePageState, context: Context): CorePageState {
  const { settings, filters } = context;
  const contextState: {
    settings: Record<string, Entity[]>;
    filters: Record<string, Entity[]>;
  } = {
    settings: {
      ...{},
      ...settings.values,
    },
    filters: filters?.values ? { ...{}, ...filters.values } : {},
  };

  if (deepEqual(state.contextState, contextState)) {
    return state;
  }

  return {
    ...{},
    ...state,
    pendingContextState: contextState,
    error: undefined,
    pageExpired: true,
  };
}

export { initialState, reducer };
